import { FiGrid, FiCheckCircle, FiFileText, FiAlertTriangle } from 'react-icons/fi';

/**
 * All marketing copy for the public landing page, in one place.
 *
 * Kept as data (not JSX) so the wording can be reviewed and edited without
 * touching layout. Claims here must stay honest — no invented metrics,
 * customer counts, or logos.
 */
export const LANDING_CONTENT = {
  nav: {
    anchors: [
      { id: 'features', label: 'Features' },
      { id: 'how-it-works', label: 'How it works' },
    ],
    signIn: 'Sign in',
    cta: 'Get started',
  },

  hero: {
    eyebrow: 'Programmed asset compliance',
    title: "Get to work. We'll handle the paperwork.",
    lede:
      'FieldCert keeps RCDs, switchboards, appliances and exit lights on schedule across every site — tested in the field, certified from the office.',
    cta: 'Get started',
    // Sites and figures in the screenshot are a demonstration dataset, not real customers.
    screenshotAlt:
      'The FieldCert dashboard showing testing compliance, site and asset totals, sessions in progress, and the sites with outstanding repairs.',
  },

  capabilities: [
    'AS/NZS 3760 compliant',
    'Works offline in the field',
    'Photo evidence on every test',
    'Audit-ready PDF certificates',
  ],

  features: {
    eyebrow: 'What you get',
    title: 'Everything the job needs. Nothing it does not.',
    items: [
      {
        icon: FiGrid,
        title: 'Asset management',
        body:
          'Every RCD, switchboard, appliance and exit light, catalogued by site and asset type. Due dates are calculated from the standard, so you always know what is next.',
      },
      {
        icon: FiCheckCircle,
        title: 'Test sessions',
        body:
          'Run a session on a phone or tablet, one asset at a time. Record trip times and results as you go. Sessions keep working with no signal and sync when you are back in range.',
      },
      {
        icon: FiFileText,
        title: 'Compliance reports',
        body:
          'Turn a finished session into an audit-ready PDF certificate. Results, dates and evidence are already attached, so nothing has to be re-typed later.',
      },
      {
        icon: FiAlertTriangle,
        title: 'Defect management',
        body:
          'Log a defect the moment you find it, with photos attached to the asset. Track it through to repair, and keep the trail that proves it was dealt with.',
      },
    ],
  },

  howItWorks: {
    eyebrow: 'How it works',
    title: 'Up and running in three steps.',
    steps: [
      {
        number: '01',
        title: 'Add your sites and assets',
        body:
          'Import an existing asset register or add sites by hand. FieldCert groups everything by site and asset type.',
      },
      {
        number: '02',
        title: 'Run test sessions in the field',
        body:
          'Work through a site on your phone. Record each result against the interval the standard requires, online or off.',
      },
      {
        number: '03',
        title: 'Generate compliance reports',
        body:
          'Close the session and export the certificate. Send it to the client, or keep it on file for the next audit.',
      },
    ],
  },

  cta: {
    title: 'Stay compliant. Stay ahead.',
    body: 'Set up your first site and run a test session today.',
    button: 'Get started',
  },

  footer: {
    blurb: 'Asset compliance for Australian electrical contractors and facility managers.',
    copyright: 'FieldCert Pty Ltd',
  },
};
